/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component } from 'react';
import { View, StyleSheet } from 'react-native';

import { fitToContain } from '../../support/Utils';

import MapView, { Marker } from '../uiold/MapView';
import LoadingIndicator from '../uiold/LoadingIndicator';

import { Text } from '../uiold';
import { themeColors } from '../uiold/Constants';

import { Market } from '../../types/shared/Market';
import { PositionMapByMarket } from '../../types/shared/PositionMap';

const ERROR_LOADING_MAP = t('Error: unable to load map');

type Position = { left: number; top: number };

type Props = {
  market: Market;
  position: PositionMapByMarket;
  onAppendMarker: (position: Position) => void;
  onUpdateMarker: (index: number, position: Position | null) => void;
};

type State = {
  isLoading: boolean;
  showError: string | null | undefined;
  imageWidth: number;
  imageHeight: number;
  containerWidth: number;
  windowSize: {
    width: number;
    height: number;
  };
};

class MapMarkerEditor extends Component<Props, State> {
  state: State = {
    isLoading: true,
    showError: null,
    imageWidth: 0,
    imageHeight: 0,
    containerWidth: 0,
    windowSize: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
  };

  componentDidMount() {
    this._loadImage();
    window.addEventListener('resize', this._windowResize);
  }

  componentDidUpdate(prevProps: Props) {
    let oldMap: any = prevProps.market.map || {};
    let newMap: any = this.props.market.map || {};
    if (oldMap.id !== newMap.id) {
      this._loadImage();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this._windowResize);
  }

  render() {
    if (this.state.showError) {
      return <p className="text-danger">{this.state.showError}</p>;
    }
    if (this.state.isLoading) {
      return <LoadingIndicator />;
    }
    // size the map so it has a max width of 100% of its container and a
    // max height of the viewport height
    let mapSize = fitToContain(
      this.state.imageWidth,
      this.state.imageHeight,
      this.state.containerWidth,
      this.state.windowSize.height,
    );
    return (
      <View
        onLayout={(event) =>
          this.setState({ containerWidth: event.nativeEvent.layout.width })
        }
      >
        {mapSize.width === 0 || mapSize.height === 0 ? null : (
          <MapView
            className="map-pane"
            width={mapSize.width}
            height={mapSize.height}
            imageUrl={this._getImageURL()}
            imageWidth={this.state.imageWidth}
            imageHeight={this.state.imageHeight}
            onMapClick={this._addMarker}
          >
            {this._renderMarkers()}
          </MapView>
        )}
      </View>
    );
  }
  _renderMarkers = (): React.ReactNode => {
    let { market, position } = this.props;
    if (
      !market.map ||
      !market.map.markers ||
      !market.map.markers.length ||
      !position
    ) {
      return null;
    }
    // TODO: Change map.markers => keyed object
    return market.map.markers.map((marker, i) => {
      if (marker == null) {
        return null;
      }
      let id = market.id;
      let mark = position[id];
      let key = mark ? mark[String(i)] : String(i + 1);
      return (
        <Marker
          key={key}
          itemKey={key as any}
          left={marker.left}
          top={marker.top}
          onClick={() => this._removeMarker(i)}
          onMove={this._moveMarker(i)}
        >
          <View style={styles.marker}>
            <Text style={styles.markerText}>{key}</Text>
          </View>
        </Marker>
      );
    });
  };

  _windowResize = () => {
    this.setState({
      windowSize: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    });
  };

  _loadImage = () => {
    // TODO: Use helpers/loadImage
    let image = new Image();
    image.onload = () => {
      this.setState({
        isLoading: false,
        imageWidth: image.width,
        imageHeight: image.height,
      });
    };
    image.onerror = () => {
      this.setState({
        showError: ERROR_LOADING_MAP,
      });
    };
    image.src = this._getImageURL();
    this.setState({ isLoading: true });
  };

  _getImageURL = () => {
    let market = this.props.market;
    let map: any = market.map || {};
    return `/api/images/${map.id}/image.${map.imageType}`;
  };

  _addMarker = (left: number, top: number) => {
    // Check bounds
    if (left < 0 || left > 10000 || top < 0 || top > 10000) {
      return;
    }
    this.props.onAppendMarker({ left, top });
  };

  _moveMarker = (index: number) => (left: number, top: number) => {
    // Check bounds
    if (left < 0 || left > 10000 || top < 0 || top > 10000) {
      return;
    }
    this.props.onUpdateMarker(index, { left, top });
  };

  _removeMarker = (index: number) => {
    this.props.onUpdateMarker(index, null);
  };
}

let styles = StyleSheet.create({
  marker: {
    position: 'absolute',
    left: '50%',
    top: '20%',
    transform: [{ translateX: '-50%' }, { translateY: '-19%' }] as any,
    height: 38,
    borderRadius: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  markerText: {
    color: themeColors.white,
    fontSize: 12,
  },
});
export default MapMarkerEditor;
